import { useEffect, useLayoutEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { useAuthService } from '~domains/auth';

import { setRegisteredQueryClient } from '~shared/lib/query-client-registry';

import { toast } from '@breezeventures/ui-kit/toast';
import { isAuthError } from '@breezeventures/web-lib-ph-request/errors';
import { formatHttpErrorMessage } from '@breezeventures/web-lib-ph-request/utils';
import { isAuthorisedAtom } from '@breezeventures/web-ph-client-auth/model';
import {
  QueryClient,
  QueryClientProvider,
  QueryMeta,
  useQueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

const shouldSkipGlobalErrorHandling = (meta: QueryMeta | undefined) => {
  return meta && 'skipGlobalErrorHandling' in meta;
};

const PortalQueryClientProviderInner = ({ children }: PropsWithChildren) => {
  const { clearSession } = useAuthService();
  const queryClient = useQueryClient();
  const isAuthorised = useAtomValue(isAuthorisedAtom);

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') {
        return;
      }

      const { error } = event.action;

      if (isAuthError(error)) {
        if (isAuthorised) {
          clearSession();
        }
        return;
      }

      if (!shouldSkipGlobalErrorHandling(event.query.options.meta)) {
        toast(
          formatHttpErrorMessage(
            'Something went wrong. Please, try again later',
            error
          ),
          {
            type: 'error',
          }
        );
      }
    });
  }, [queryClient, clearSession, isAuthorised]);

  return <>{children}</>;
};

export const PortalQueryClientProvider = ({ children }: PropsWithChildren) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: (retryCount: number, error: Error) => {
              if (isAuthError(error)) return false;
              return retryCount <= 3;
            },
          },
          mutations: {
            retry: false,
          },
        },
      })
  );

  useLayoutEffect(() => {
    setRegisteredQueryClient(queryClient);
    return () => setRegisteredQueryClient(undefined);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <PortalQueryClientProviderInner>
        {children}
      </PortalQueryClientProviderInner>
    </QueryClientProvider>
  );
};
